import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { fmtAUD, fmtDate } from '@/lib/format';
import { useAuth } from '@/context/AuthContext';
import SupplierEmail from '@/components/SupplierEmail';
import { Toaster } from 'sonner';

const STATUS_COLORS = {
    submitted: 'text-amber-400 border-amber-400/30',
    paid: 'text-emerald-400 border-emerald-400/30',
    fulfilled: 'text-blue-400 border-blue-400/30',
    cancelled: 'text-zinc-500 border-zinc-700',
};

export default function Orders() {
    const { user } = useAuth();
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expanded, setExpanded] = useState(null);

    useEffect(() => {
        api.get('/orders').then((r) => setOrders(r.data)).finally(() => setLoading(false));
    }, []);

    return (
        <div data-testid="orders-page">
            <Toaster theme="dark" />
            <div className="font-mono-data text-xs uppercase text-brand tracking-widest mb-2">
                {user?.role === 'admin' ? 'All Orders' : 'Your History'}
            </div>
            <h1 className="font-display text-4xl uppercase tracking-tighter mb-8">Orders</h1>

            {loading ? (
                <div className="text-zinc-400 font-mono-data">Loading…</div>
            ) : orders.length === 0 ? (
                <div className="border-hairline p-10 text-center text-zinc-400">No orders yet.</div>
            ) : (
                <div className="border-hairline">
                    <div className="grid grid-cols-12 font-mono-data text-[10px] uppercase text-zinc-500 px-5 py-3 border-b border-zinc-800 bg-surface">
                        <div className="col-span-3">Order</div>
                        <div className="col-span-3">Academy</div>
                        <div className="col-span-2">Date</div>
                        <div className="col-span-2 text-right">Total</div>
                        <div className="col-span-2 text-right">Status</div>
                    </div>
                    {orders.map((o) => (
                        <div key={o.id} className="border-b border-zinc-800/60" data-testid={`order-row-${o.id}`}>
                            <button onClick={() => setExpanded(expanded === o.id ? null : o.id)}
                                    className="w-full grid grid-cols-12 items-center px-5 py-4 hover:bg-surface/60 transition-colors text-left">
                                <div className="col-span-3 font-mono-data text-xs">{o.id.slice(0, 8)}…</div>
                                <div className="col-span-3 font-display uppercase text-sm">{o.academy_name}</div>
                                <div className="col-span-2 font-mono-data text-xs text-zinc-400">{fmtDate(o.created_at)}</div>
                                <div className="col-span-2 text-right font-mono-data text-sm">{fmtAUD(o.subtotal)}</div>
                                <div className="col-span-2 text-right">
                                    <span className={`inline-block px-2 py-1 border font-mono-data text-[10px] uppercase ${STATUS_COLORS[o.status] || 'text-zinc-400 border-zinc-700'}`}>
                                        {o.status}
                                    </span>
                                </div>
                            </button>
                            {expanded === o.id && (
                                <div className="px-5 py-4 bg-surface/40 border-t border-zinc-800/60" data-testid={`order-detail-${o.id}`}>
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4 font-mono-data text-xs">
                                        <div><div className="text-zinc-500 uppercase text-[10px]">Contact</div>{o.contact_name}</div>
                                        <div><div className="text-zinc-500 uppercase text-[10px]">Email</div>{o.contact_email || '—'}</div>
                                        <div><div className="text-zinc-500 uppercase text-[10px]">Quote ref</div>{o.quote_ref || '—'}</div>
                                        <div><div className="text-zinc-500 uppercase text-[10px]">Payment</div>{o.payment_status}</div>
                                    </div>
                                    {o.notes && <div className="mb-4 font-body text-sm text-zinc-300">Notes: {o.notes}</div>}
                                    <div className="border border-zinc-800 mb-5">
                                        {o.lines.map((l, idx) => (
                                            <div key={idx} className="grid grid-cols-12 px-3 py-2 border-b border-zinc-800/60 last:border-0 font-mono-data text-xs">
                                                <div className="col-span-6">{l.product_name} <span className="text-zinc-500">· {l.variant_label}</span></div>
                                                <div className="col-span-2 text-center">× {l.qty}</div>
                                                <div className="col-span-2 text-right">{fmtAUD(l.unit_price)}</div>
                                                <div className="col-span-2 text-right">{fmtAUD(l.line_total)}</div>
                                            </div>
                                        ))}
                                    </div>
                                    <SupplierEmail orderId={o.id} />
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
